<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-row align-h="between" class="mb-1">
                        <b-col cols="10" md="5">
                            <b-link :to="{ name: 'settings' }">
                                <font-awesome-icon icon="arrow-left" />
                                {{ $t("Back") }}
                            </b-link>
                            <h1 class="mt-1 mb-2">
                                {{ $t("mainmenu.settings") }} /
                                {{ $t("settings.channels.Channels") }}
                            </h1>
                        </b-col>

                        <b-col class="text-right align-content-end" md="5" sm="12">
                            <EmbeddedSignupButton v-if="$can('update', 'customerSettings')" />
                            <BirdEmbeddedSignupButton v-if="$can('update', 'customerSettings')" />
                            <ChatwootButton v-if="$can('update', 'customerSettings')" />
                        </b-col>
                    </b-row>

                    <div v-if="!isLoading" class="mb-2">
                        <b-row align-h="between">
                            <b-col cols="12" md="2">
                                <b-form-input
                                    v-model="searchQuery"
                                    :placeholder="$t('paginator.search')"
                                    class="d-inline-block mr-1 bg-light"
                                    debounce="500"
                                />
                            </b-col>

                            <b-col cols="auto">
                                <label>{{ $t("paginator.show") }}</label>
                                <v-select
                                    v-model="perPage"
                                    :clearable="false"
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                    :options="perPageOptions"
                                    class="per-page-selector d-inline-block mx-50"
                                />
                                <label>{{ $t("paginator.entries") }}</label>
                            </b-col>
                        </b-row>
                    </div>

                    <b-table
                        ref="refTable"
                        :empty-text="$t('paginator.no-matching-records-found')"
                        :fields="tableColumns"
                        :items="read"
                        :no-sort-reset="true"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="isSortDirDesc"
                        :table-busy="isLoading"
                        class="position-relative"
                        primary-key="id"
                        responsive
                        show-empty
                    >
                        <template #table-busy>
                            <div class="d-flex justify-content-center my-1">
                                <b-spinner />
                            </div>
                        </template>

                        <!-- Column: Name -->
                        <template #cell(name)="data">
                            <strong>{{ data.item.name }}</strong>
                        </template>

                        <!-- Column: Platform -->
                        <template #cell(platform)="data">
                            <span class="text-secondary">
                                <font-awesome-icon
                                    :icon="resolvePlatformIcon(data.item.provider, data.item.platform)"
                                    size="lg"
                                />
                                {{ transformPlatformName(data.item.platform) }}
                            </span>
                        </template>

                        <!-- Column: Active -->
                        <template #cell(active)="data">
                            <!--b-form-checkbox
                                :checked="data.item.active === 1"
                                class="custom-control-success"
                                name="check-button"
                                switch
                                disabled
                            /-->
                            <div class="text-center">
                                <font-awesome-icon
                                    :class="[data.item.active === 1 ? 'text-success' : 'text-danger']"
                                    icon="circle"
                                />
                            </div>
                        </template>

                        <!-- Column: Actions -->
                        <template #cell(actions)="data">
                            <div class="text-right">
                                <b-button
                                    v-if="$can('update', 'channels')"
                                    :to="{
                                        name: 'settings-channels-edit',
                                        params: { id: data.item.id },
                                    }"
                                    class="btn-icon mr-50"
                                    variant="light text-secondary"
                                >
                                    <font-awesome-icon icon="pen" />
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                    <div v-if="!isLoading" class="mx-2 mb-2">
                        <b-row>
                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-start"
                                cols="12"
                                sm="6"
                            >
                                <span class="text-muted">
                                    {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }}
                                    {{ dataMeta.of }}
                                    {{ $t("paginator.entries") }}
                                </span>
                            </b-col>

                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-end"
                                cols="12"
                                sm="6"
                            >
                                <b-pagination
                                    v-model="currentPage"
                                    :per-page="perPage"
                                    :total-rows="total"
                                    class="mb-0 mt-1 mt-sm-0"
                                    first-number
                                    last-number
                                    next-class="next-item"
                                    prev-class="prev-item"
                                    size="sm"
                                >
                                    <template #prev-text>
                                        <font-awesome-icon icon="chevron-left" size="lg" />
                                    </template>
                                    <template #next-text>
                                        <font-awesome-icon icon="chevron-right" size="lg" />
                                    </template>
                                </b-pagination>
                            </b-col>
                        </b-row>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import {
    BButton,
    BCard,
    BCardText,
    BCol,
    BContainer,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BFormInput,
    BLink,
    BPagination,
    BRow,
    BSpinner,
    BTab,
    BTable,
    BTabs,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { resolvePlatformIcon, transformPlatformName } from "@/utils/channels";
import useChannelsList from "./useChannelsList";
import EmbeddedSignupButton from "@/views/modules/settings/channels/components/embeddedSignupButton.vue";
import BirdEmbeddedSignupButton from "@/views/modules/settings/channels/components/birdEmbeddedSignupButton.vue";
import ChatwootButton from "@/views/modules/settings/channels/components/chatwootButton.vue";

export default {
    components: {
        EmbeddedSignupButton,
        BirdEmbeddedSignupButton,
        ChatwootButton,
        BContainer,
        BCard,
        BRow,
        BCol,
        BFormInput,
        BFormCheckbox,
        BButton,
        BTable,
        BDropdown,
        BDropdownItem,
        BPagination,
        BCardText,
        BTabs,
        BTab,
        BLink,
        BSpinner,
        vSelect,
    },
    setup() {
        const {
            read,
            refetchData,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
            isLoading,
        } = useChannelsList();

        return {
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
            isLoading,
            read,
            refetchData,
            resolvePlatformIcon,
            transformPlatformName,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-select.scss";
</style>
